


import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import api from "../chat/api";

// Shown while removing members who own projects.
//
// Rename, archive, delete and share are owner-only, so a member who leaves takes
// their projects out of everyone's reach. This asks who should take them over
// before the removal goes through.
//
// Two ways to answer, because both are useful: "assign everything to one person"
// for the common case, and a per-project override for when different projects
// should go to different people.
const ReassignProjectsModal = ({
  isOpen,
  onClose,
  onConfirm,
  departingMembers = [],
  candidates = [],
  organizationId,
  removing = false,
  error = "",
}) => {
  const [projects, setProjects] = useState([]);
  const [assignments, setAssignments] = useState({}); // folder_id -> new owner id
  const [bulkOwnerId, setBulkOwnerId] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [query, setQuery] = useState("");

  const departingIds = useMemo(
    () => departingMembers.map((m) => m.user_id).filter(Boolean),
    [departingMembers]
  );
  const departingKey = departingIds.join(",");

  // Whoever is leaving can't inherit the projects — they'd freeze again.
  const eligible = useMemo(
    () => candidates.filter((c) => c.user_id && !departingIds.includes(c.user_id)),
    [candidates, departingIds]
  );

  useEffect(() => {
    if (!isOpen || !departingKey || !organizationId) return undefined;

    // Reopening for a different selection while a slower request is still in
    // flight must not let the stale answer land on top of the new one.
    let cancelled = false;
    setLoading(true);
    setLoadError("");

    api
      .get(
        `/api/v1/org/members/owned-projects/?organization_id=${encodeURIComponent(
          organizationId
        )}&user_ids=${encodeURIComponent(departingKey)}`
      )
      .then((res) => {
        if (cancelled) return;
        setProjects(res.data?.projects || []);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError("Couldn't load their projects. You can still remove them and reassign later.");
        setProjects([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, departingKey, organizationId]);

  // Reset when a different set of members is being removed.
  useEffect(() => {
    setAssignments({});
    setBulkOwnerId("");
    setQuery("");
  }, [departingKey]);

  // Someone who owns a lot of projects would otherwise be an unscannable wall of
  // dropdowns, so the list is filterable by project or current owner.
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter(
      (p) =>
        (p.name || "").toLowerCase().includes(q) ||
        (p.owner_name || "").toLowerCase().includes(q)
    );
  }, [projects, query]);

  // Applies to what is currently on screen, which makes "filter, then assign the
  // batch" a way to split projects between people. Assignments made outside the
  // current filter are merged, never overwritten.
  const applyToAll = (ownerId) => {
    setBulkOwnerId(ownerId);
    if (!ownerId) return;
    setAssignments((prev) => ({
      ...prev,
      ...Object.fromEntries(visible.map((p) => [p.folder_id, Number(ownerId)])),
    }));
  };

  const assignedCount = Object.values(assignments).filter(Boolean).length;
  const filtering = query.trim().length > 0;
  const names = departingMembers.map((m) => m.name || m.email).filter(Boolean).join(", ");

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center font-outfit">
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
        onClick={!removing ? onClose : undefined}
      />
      <div className="relative bg-white rounded-2xl shadow-xl w-[620px] max-h-[85vh] flex flex-col px-6 py-5">
        <h2 className="text-[#18181B] text-[20px] font-semibold mb-1">
          Reassign projects before removing
        </h2>
        <p className="text-[#71717A] text-[14px] leading-relaxed mb-4">
          {loading ? (
            "Checking what they own…"
          ) : projects.length === 0 ? (
            <>
              <span className="font-medium text-[#3F3F46]">{names}</span> doesn't own any projects
              in this workspace, so nothing needs handing over.
            </>
          ) : (
            <>
              <span className="font-medium text-[#3F3F46]">{names}</span> owns{" "}
              <span className="font-medium text-[#3F3F46]">{projects.length}</span>{" "}
              {projects.length === 1 ? "project" : "projects"}. Only an owner can rename, archive,
              delete or share a project, so anything left behind will be stuck until someone takes
              it over.
            </>
          )}
        </p>

        {projects.length > 0 && (
          <>
            {projects.length > 5 && (
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search projects…"
                className="mb-3 w-full px-3 py-2 border border-[#E4E4E7] rounded-lg text-[14px] outline-none focus:border-[#C95EBE]"
              />
            )}

            <div className="flex items-center gap-3 mb-3 p-3 bg-[#FAFAFA] border border-[#E4E4E7] rounded-lg">
              <span className="text-[14px] text-[#3F3F46] font-medium whitespace-nowrap">
                {filtering ? `Assign these ${visible.length} to` : "Assign all to"}
              </span>
              <select
                value={bulkOwnerId}
                onChange={(e) => applyToAll(e.target.value)}
                className="flex-1 px-3 py-1.5 border border-[#E4E4E7] rounded-md text-[14px] bg-white outline-none focus:border-[#C95EBE]"
              >
                <option value="">Choose a member…</option>
                {eligible.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.full_name || m.email}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1 overflow-y-auto border border-[#E4E4E7] rounded-lg divide-y divide-[#F4F4F5] min-h-0">
              {visible.length === 0 && (
                <p className="px-3 py-4 text-[13px] text-[#A1A1AA]">
                  No projects match “{query.trim()}”.
                </p>
              )}
              {visible.map((p) => (
                <div key={p.folder_id} className="flex items-center gap-3 px-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <p className="text-[14px] text-[#18181B] truncate">{p.name}</p>
                    <p className="text-[12px] text-[#A1A1AA] truncate">
                      {p.owner_name}
                      {p.is_archived ? " · archived" : ""}
                    </p>
                  </div>
                  <select
                    value={assignments[p.folder_id] || ""}
                    onChange={(e) =>
                      setAssignments((prev) => ({
                        ...prev,
                        [p.folder_id]: e.target.value ? Number(e.target.value) : undefined,
                      }))
                    }
                    className="w-[220px] px-2 py-1.5 border border-[#E4E4E7] rounded-md text-[13px] bg-white outline-none focus:border-[#C95EBE]"
                  >
                    <option value="">Leave unassigned</option>
                    {eligible.map((m) => (
                      <option key={m.user_id} value={m.user_id}>
                        {m.full_name || m.email}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </>
        )}

        {(loadError || error) && (
          <p className="text-[13px] text-[#E21919] mt-3">{loadError || error}</p>
        )}

        <div className="flex justify-between items-center gap-3 mt-5">
          <span className="text-[13px] text-[#A1A1AA]">
            {projects.length > 0
              ? `${assignedCount} of ${projects.length} assigned${
                  filtering ? ` · showing ${visible.length}` : ""
                }`
              : ""}
          </span>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={removing}
              className="px-5 py-2 rounded-md border border-[#E4E4E7] text-[#52525B] text-[14px] font-medium hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={() => onConfirm(assignments)}
              disabled={removing || loading}
              className="px-5 py-2 rounded-md bg-[#E95353] text-white text-[14px] font-semibold hover:bg-[#d64545] transition-colors disabled:opacity-60"
            >
              {removing
                ? "Removing…"
                : assignedCount > 0
                ? "Reassign & remove"
                : "Remove anyway"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ReassignProjectsModal;
